/** @file Onion skins (red before, green after, fading with distance) and the diff ghost with drift lines. */
import type { Sink } from '../draw/createSink';
import { emitLine } from '../draw/emitLine';
import { fillSegment } from '../draw/fillSegment';
import { scaleAlpha } from '../draw/scaleAlpha';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { boneSegment } from './boneSegment';
import { drawGhostBones } from './drawGhostBones';

const now = new Float64Array(4);
const then = new Float64Array(4);
const line = new Float64Array(4);

/** A frame count: non-finite or negative reads as 0 (R7-03). */
const frames = (n: number): number => (Number.isFinite(n) ? Math.max(0, n) : 0);

/**
 * `options.onion` with `input.onionPoses` (before..after in time order): the first `before` poses are earlier
 * frames, the next `after` later ones; a non-finite count is 0, so that side draws nothing (R7-03).
 */
function drawOnion(sink: Sink, input: OverlayInput): void {
  const onion = input.options.onion;
  if (onion === undefined) return;
  const nBefore = frames(onion.before);
  (input.onionPoses ?? []).slice(0, nBefore + frames(onion.after)).forEach((pose, i) => {
    const before = i < nBefore;
    const distance = before ? nBefore - i : i - nBefore + 1;
    const alpha = Math.max(0.15, 0.6 - 0.15 * (distance - 1));
    drawGhostBones(sink, input, pose, scaleAlpha(before ? OVERLAY_STYLE.onionBefore : OVERLAY_STYLE.onionAfter, alpha));
  });
}

/** Drift line from the other pose's point k (0 = joint, 2 = tip) to the live one, when at least 1 px long. */
function drift(sink: Sink, k: number): void {
  fillSegment(line, then[k] ?? NaN, then[k + 1] ?? NaN, now[k] ?? NaN, now[k + 1] ?? NaN);
  const d = Math.hypot((line[2] ?? 0) - (line[0] ?? 0), (line[3] ?? 0) - (line[1] ?? 0));
  if (d >= 1) emitLine(sink, line, 1, scaleAlpha(OVERLAY_STYLE.diff, 0.6));
}

/** `input.diffPose` (a `diff:<rev>` render): the other revision as a magenta ghost plus joint/tip drift >= 1 px. */
function drawDiff(sink: Sink, input: OverlayInput): void {
  const other = input.diffPose;
  if (other === undefined) return;
  drawGhostBones(sink, input, other, scaleAlpha(OVERLAY_STYLE.diff, 0.8));
  input.data.bones.forEach((bone, i) => {
    if (bone.kind !== 'bone') return;
    boneSegment(input.pose, input.camera, i, bone.length, now);
    boneSegment(other, input.camera, i, bone.length, then);
    drift(sink, 0);
    if (bone.length > 0) drift(sink, 2);
  });
}

/** Ghost layers, drawn under the live skeleton. */
export function drawGhosts(sink: Sink, input: OverlayInput): void {
  drawOnion(sink, input);
  drawDiff(sink, input);
}
